import React, { useState, useEffect, useMemo, useRef, useLayoutEffect } from 'react';
import BreadCrumb from 'Common/BreadCrumb';
import { DashboardService } from '../../services/dashboardService';
import { SchoolsService } from '../../services/schoolsService';
import { Student } from '../../types/student';
import { School } from '../../types/school';
import EditStudentDialog from './EditStudentDialog';
import { toast } from 'react-toastify';

import { Search, Pencil, Filter } from 'lucide-react';

const Students = () => {
    const [students, setStudents] = useState<Student[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState<Student[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
    const [schools, setSchools] = useState<School[]>([]);
    const [selectedSchoolId, setSelectedSchoolId] = useState<string>('all');
    const [searchQuery, setSearchQuery] = useState('');

    const pageSize = 10;

    useEffect(() => {
        loadStudents();
        loadSchools();
    }, []);

    const loadStudents = async () => {
        try {
            setIsLoading(true);
            // Get all students - let TableContainer handle pagination
            const result = await DashboardService.getAllStudents(1000); // Large number to get all
            const allStudents = result.students;
            setStudents(allStudents);
            setData(allStudents);
            console.log('Loaded students:', allStudents.length, allStudents);
        } catch (error) {
            console.error('Error loading students:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const loadSchools = async () => {
        try {
            const schoolsData = await SchoolsService.getAllSchools();
            setSchools(schoolsData);
        } catch (error) {
            console.error('Error loading schools:', error);
        }
    };

    useEffect(() => {
        let filtered = students;

        if (selectedSchoolId !== 'all') {
            filtered = filtered.filter(student => {
                if (student.schoolId) {
                    return student.schoolId === selectedSchoolId;
                }
                const school = schools.find(s => s.id === selectedSchoolId);
                return school && student.schoolName === school.name;
            });
        }

        if (searchQuery.trim()) {
            const search = searchQuery.toLowerCase();
            filtered = filtered.filter(student =>
                student.fullName.toLowerCase().includes(search) ||
                student.email.toLowerCase().includes(search) ||
                student.userName.toLowerCase().includes(search)
            );
        }

        setData(filtered);
        setCurrentPage(1);
    }, [selectedSchoolId, searchQuery, students, schools]);

    const filterSearchData = (e: any) => {
        setSearchQuery(e.target.value);
    };
    const selectedSchoolName = useMemo(() => {
        if (selectedSchoolId === 'all') return 'All Schools';
        return schools.find(school => school.id === selectedSchoolId)?.name ?? 'All Schools';
    }, [selectedSchoolId, schools]);

    // Size the filter to its selected label so long school names aren't clipped.
    // A hidden span mirrors the label and is measured, since a native <select>
    // otherwise sizes to its widest option rather than the selected one.
    const schoolLabelRef = useRef<HTMLSpanElement>(null);
    const [schoolFilterWidth, setSchoolFilterWidth] = useState<number | null>(null);

    useLayoutEffect(() => {
        let cancelled = false;
        const measure = () => {
            if (cancelled || !schoolLabelRef.current) return;
            // Text width + the icon padding (pl-10) and the native dropdown arrow.
            setSchoolFilterWidth(Math.ceil(schoolLabelRef.current.getBoundingClientRect().width) + 80);
        };
        measure();
        // Re-measure once the webfont lands, otherwise the first pass measures a fallback face.
        document.fonts?.ready.then(measure).catch(() => { });
        return () => { cancelled = true; };
    }, [selectedSchoolName]);
    const handleEditStudent = (student: Student) => {
        setSelectedStudent(student);
        setShowEditDialog(true);
    };

    const handleCloseEditDialog = () => {
        setShowEditDialog(false);
        setSelectedStudent(null);
    };

    const handleSaveStudent = async (updatedStudent: Student) => {
        try {
            await DashboardService.updateStudent(updatedStudent);

            const updatedStudents = students.map(student =>
                student.id === updatedStudent.id ? updatedStudent : student
            );
            setStudents(updatedStudents);
            setData(updatedStudents);

            toast.success('Student updated successfully!');
        } catch (error: any) {
            console.error('Error updating student:', error);
            const errorMessage = error?.message || 'Failed to update student';
            toast.error(`Error: ${errorMessage}`);
            throw error; // Re-throw to let the dialog handle it
        }
    };


    return (
        <React.Fragment>
            <BreadCrumb title='Students' pageTitle='Dashboard' />
            <div className="col-span-12 card">
                <div className="card-body">
                    <div className="flex flex-col gap-4 mb-5 md:flex-row md:items-center md:justify-between">
                        <div>
                            <h6 className="text-15 font-semibold">All Students</h6>
                            <p className="text-slate-500 dark:text-zink-200 text-sm mt-1">
                                Showing {data.length} of {students.length} students
                            </p>
                        </div>
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                            <div className="relative w-full sm:w-64">
                                <input 
                                    type="text" 
                                    className="ltr:pl-10 rtl:pr-10 form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200" 
                                    placeholder="Search students..." 
                                    autoComplete="off" 
                                    onChange={(e) => filterSearchData(e)} 
                                />
                                <Search className="inline-block size-4 absolute ltr:left-3 rtl:right-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-zink-200" />
                            </div>
                            <div
                                className="relative w-full sm:w-[var(--school-filter-w)] sm:min-w-48 sm:max-w-xs"
                                style={{ '--school-filter-w': schoolFilterWidth ? `${schoolFilterWidth}px` : '12rem' } as React.CSSProperties}
                            >
                                {/* Mirrors the selected label off-screen purely so it can be measured */}
                                <span
                                    ref={schoolLabelRef}
                                    aria-hidden="true"
                                    className="absolute top-0 ltr:left-0 rtl:right-0 invisible whitespace-pre text-base pointer-events-none"
                                >
                                    {selectedSchoolName}
                                </span>
                                <select
                                    value={selectedSchoolId}
                                    onChange={(e) => setSelectedSchoolId(e.target.value)}
                                    className="ltr:pl-10 rtl:pr-10 truncate form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 cursor-pointer"
                                >
                                    <option value="all">All Schools</option>
                                    {schools.map((school) => (
                                        <option key={school.id} value={school.id}>
                                            {school.name}
                                        </option>
                                    ))}
                                </select>
                                <Filter className="inline-block size-4 absolute ltr:left-3 rtl:right-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-zink-200 pointer-events-none" />
                            </div>
                        </div>
                    </div>
                    {isLoading && students.length === 0 ? (
                        <div className="flex items-center justify-center h-32">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                        </div>
                    ) : (
                        <div>
                            <div className="overflow-x-auto">
                                <table className="w-full whitespace-nowrap">
                                    <thead className="ltr:text-left rtl:text-right bg-slate-100 text-slate-500 dark:text-zink-200 dark:bg-zink-600">
                                        <tr>
                                            <th className="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold border-y border-slate-200 dark:border-zink-500">
                                                Student
                                            </th>
                                            <th className="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold border-y border-slate-200 dark:border-zink-500">
                                                Username
                                            </th>
                                            <th className="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold border-y border-slate-200 dark:border-zink-500">
                                                Email
                                            </th>
                                            <th className="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold border-y border-slate-200 dark:border-zink-500">
                                                Status
                                            </th>
                                            <th className="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold border-y border-slate-200 dark:border-zink-500">
                                                Action
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.slice((currentPage - 1) * pageSize, currentPage * pageSize).map((student, index) => (
                                            <tr key={student.id}>
                                                <td className="px-3.5 py-2.5 first:pl-5 last:pr-5 border-t border-slate-200 dark:border-zink-500 last:border-b-0 flex items-center">
                                                    <div className="flex gap-2 items-center">
                                                        <div className="size-10 bg-green-100 rounded-full dark:bg-green-500/20 shrink-0">
                                                            <img src={student.avatarUrl} alt="" className="h-10 rounded-full" onError={(e) => {
                                                                e.currentTarget.style.display = 'none';
                                                                const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                                                                if (nextElement) nextElement.style.display = 'flex';
                                                            }} />
                                                            <div className="size-10 bg-gray-100 rounded-full dark:bg-gray-500/20 shrink-0 items-center justify-center hidden" style={{display: 'none'}}>
                                                                <span className="text-gray-500">👤</span>
                                                            </div>
                                                        </div>
                                                        <div className="grow">
                                                            <h6 className="flex items-center h-full">{student.fullName}</h6>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-3.5 py-2.5 first:pl-5 last:pr-5 border-t border-slate-200 dark:border-zink-500 last:border-b-0">
                                                    @{student.userName}
                                                </td>
                                                <td className="px-3.5 py-2.5 first:pl-5 last:pr-5 border-t border-slate-200 dark:border-zink-500 last:border-b-0">
                                                    {student.email}
                                                </td>
                                                <td className="px-3.5 py-2.5 first:pl-5 last:pr-5 border-t border-slate-200 dark:border-zink-500 last:border-b-0">
                                                    {student.deleted ?
                                                        <span className="px-2.5 py-0.5 text-xs inline-block font-medium rounded border bg-red-100 border-red-200 text-red-500 dark:bg-red-500/20 dark:border-red-500/20">Deactivated</span> :
                                                        <span className="px-2.5 py-0.5 text-xs inline-block font-medium rounded border bg-green-100 border-green-200 text-green-500 dark:bg-green-500/20 dark:border-green-500/20">Active</span>
                                                    }
                                                </td>
                                                <td className="px-3.5 py-2.5 first:pl-5 last:pr-5 border-t border-slate-200 dark:border-zink-500 last:border-b-0">
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={() => handleEditStudent(student)}
                                                            className="flex items-center justify-center size-8 transition-all duration-200 ease-linear rounded-md bg-slate-100 dark:bg-zink-600 dark:text-zink-200 text-slate-500 hover:text-custom-500 dark:hover:text-custom-500 hover:bg-custom-100 dark:hover:bg-custom-500/20"
                                                        >
                                                            <Pencil className="size-4" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="flex flex-col items-center justify-between mt-5 md:flex-row">
                                <div className="mb-4 md:mb-0">
                                    <p className="text-slate-500 dark:text-zink-200">
                                        Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, data.length)} of {data.length} Results
                                    </p>
                                </div>
                                <div className="flex items-center gap-2 ml-auto">
                                    <button
                                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                        disabled={currentPage === 1}
                                        className="px-3 py-1 text-sm bg-white border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-zink-700 dark:border-zink-500 dark:hover:bg-zink-600"
                                    >
                                        Previous
                                    </button>
                                    <span className="text-sm text-slate-600 dark:text-zink-300">
                                        Page {currentPage} of {Math.ceil(data.length / pageSize)}
                                    </span>
                                    <button
                                        onClick={() => setCurrentPage(prev => Math.min(Math.ceil(data.length / pageSize), prev + 1))}
                                        disabled={currentPage === Math.ceil(data.length / pageSize)}
                                        className="px-3 py-1 text-sm bg-white border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-zink-700 dark:border-zink-500 dark:hover:bg-zink-600"
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {selectedStudent && (
                <EditStudentDialog
                    student={selectedStudent}
                    onClose={handleCloseEditDialog}
                    onSave={handleSaveStudent}
                    show={showEditDialog}
                />
            )}
        </React.Fragment>
    );
};

export default Students;